import { useDraggable } from '@dnd-kit/core'
import {
  Type, AlignLeft, Hash, Calendar, List, CheckSquare, CircleDot,
  Table, Calculator, PenTool, Camera, Paperclip, Eye, Layout,
  Cpu, HardDrive, Search
} from 'lucide-react'
import type { FieldType } from '../../../types/schede'
import { FIELD_PALETTE } from '../../../types/schede'

const ICON_MAP: Record<string, React.ElementType> = {
  'type': Type,
  'align-left': AlignLeft,
  'hash': Hash,
  'calendar': Calendar,
  'list': List,
  'check-square': CheckSquare,
  'circle-dot': CircleDot,
  'table': Table,
  'calculator': Calculator,
  'pen-tool': PenTool,
  'camera': Camera,
  'paperclip': Paperclip,
  'eye': Eye,
  'layout': Layout,
  'cpu': Cpu,
  'hard-drive': HardDrive,
  'search': Search,
}

interface FieldPaletteProps {
  onAddField: (type: FieldType, position?: number) => void
}

const CATEGORY_LABELS: Record<string, string> = {
  base: 'Base',
  avanzato: 'Avanzato',
  layout: 'Layout',
  machine: 'Macchine (v2)',
}

/** Singolo item draggabile nella palette */
function DraggablePaletteItem({ type, label, icon, isPhase2, onAddField }: {
  type: FieldType
  label: string
  icon: string
  isPhase2: boolean
  onAddField: (type: FieldType) => void
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${type}`,
    data: { type, source: 'palette' },
    disabled: isPhase2,
  })

  const Icon = ICON_MAP[icon] || Type

  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onClick={() => !isPhase2 && onAddField(type)}
      disabled={isPhase2}
      className="flex items-center gap-2 w-full px-2.5 py-2 rounded-lg text-xs font-medium transition-all hover:shadow-sm disabled:opacity-30 disabled:cursor-not-allowed touch-none"
      style={{
        backgroundColor: 'var(--color-bg-card)',
        color: isPhase2 ? 'var(--color-text-tertiary)' : 'var(--color-text-primary)',
        border: '1px solid var(--color-border)',
        opacity: isDragging ? 0.4 : 1,
        cursor: isPhase2 ? 'not-allowed' : 'grab',
      }}
      title={isPhase2 ? 'Disponibile nella v2' : `Trascina o clicca per aggiungere ${label}`}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--color-primary)' }} />
      <span className="truncate">{label}</span>
    </button>
  )
}

export default function FieldPalette({ onAddField }: FieldPaletteProps) {
  const categories = ['base', 'avanzato', 'layout', 'machine'] as const

  return (
    <div className="p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide mb-3" style={{ color: 'var(--color-text-tertiary)' }}>
        Campi
      </h3>

      {categories.map(cat => {
        const items = FIELD_PALETTE.filter(f => f.category === cat)
        if (items.length === 0) return null

        return (
          <div key={cat} className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: 'var(--color-text-tertiary)' }}>
              {CATEGORY_LABELS[cat]}
            </p>
            <div className="space-y-1">
              {items.map(item => (
                <DraggablePaletteItem
                  key={item.type}
                  type={item.type}
                  label={item.label}
                  icon={item.icon}
                  isPhase2={item.phase === 2}
                  onAddField={onAddField}
                />
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}
